import {
  parts
} from "./chunk-LOYXQC7S.mjs";
import {
  dom
} from "./chunk-RKXLOWML.mjs";

// src/checkbox.connect.ts
import { ariaAttr, dataAttr } from "@zag-js/dom-query";
import { visuallyHiddenStyle } from "@zag-js/visually-hidden";
function connect(state, send, normalize) {
  const isChecked = state.matches("checked");
  const isInteractive = state.context.isInteractive;
  const isIndeterminate = state.context.indeterminate;
  const isDisabled = state.context.disabled;
  const isInvalid = state.context.invalid;
  const isReadOnly = state.context.readOnly;
  const isRequired = state.context.required;
  const isFocusable = state.context.focusable;
  const isActive = state.context.active;
  const isHovered = state.context.hovered;
  const isFocused = !isDisabled && state.context.focused;
  const ariaLabel = state.context["aria-label"];
  const ariaLabelledBy = state.context["aria-labelledby"] ?? dom.getLabelId(state.context);
  const ariaDescribedBy = state.context["aria-describedby"];
  const name = state.context.name;
  const form = state.context.form;
  const value = state.context.value;
  const trulyDisabled = isDisabled && !isFocusable;
  const stateView = state.matches("checked") ? "checked" : "unchecked";
  const view = state.context.indeterminate ? "mixed" : stateView;
  return {
    /**
     * Whether the checkbox is checked
     */
    isChecked,
    /**
     * Whether the checkbox is disabled
     */
    isDisabled,
    /**
     * Whether the checkbox is indeterminate
     */
    isIndeterminate,
    /**
     * Whether the checkbox is focused
     */
    isFocused,
    /**
     * Whether the checkbox is readonly
     */
    isReadOnly,
    /**
     * The current view of the checkbox (checked, unchecked, mixed)
     */
    view,
    /**
     * Function to set the checked state of the checkbox
     */
    setChecked(checked) {
      send({ type: "SET_STATE", checked, manual: true });
    },
    /**
     * Function to set the indeterminate state of the checkbox
     */
    setIndeterminate(indeterminate) {
      send({ type: "SET_INDETERMINATE", value: indeterminate });
    },
    rootProps: normalize.label({
      ...parts.root.attrs,
      id: dom.getRootId(state.context),
      htmlFor: dom.getInputId(state.context),
      "data-focus": dataAttr(isFocused),
      "data-disabled": dataAttr(isDisabled),
      "data-checked": dataAttr(isChecked),
      "data-hover": dataAttr(isHovered),
      "data-invalid": dataAttr(isInvalid),
      "data-readonly": dataAttr(isReadOnly),
      onPointerMove() {
        if (!isInteractive)
          return;
        send({ type: "SET_HOVERED", value: true });
      },
      onPointerLeave() {
        if (!isInteractive)
          return;
        send({ type: "SET_HOVERED", value: false });
      },
      onPointerDown(event) {
        if (!isInteractive)
          return;
        if (isFocused && event.pointerType === "mouse") {
          event.preventDefault();
        }
        send({ type: "SET_ACTIVE", value: true });
      },
      onPointerUp() {
        if (!isInteractive)
          return;
        send({ type: "SET_ACTIVE", value: false });
      }
    }),
    labelProps: normalize.element({
      ...parts.label.attrs,
      id: dom.getLabelId(state.context),
      "data-focus": dataAttr(isFocused),
      "data-hover": dataAttr(isHovered),
      "data-readonly": dataAttr(isReadOnly),
      "data-disabled": dataAttr(isDisabled),
      "data-checked": dataAttr(isChecked),
      "data-invalid": dataAttr(isInvalid)
    }),
    controlProps: normalize.element({
      ...parts.control.attrs,
      id: dom.getControlId(state.context),
      "data-focus": dataAttr(isFocused),
      "data-disabled": dataAttr(isDisabled),
      "data-hover": dataAttr(isHovered),
      "data-indeterminate": dataAttr(isIndeterminate),
      "data-invalid": dataAttr(isInvalid),
      "data-checked": dataAttr(isChecked),
      "data-readonly": dataAttr(isReadOnly),
      "aria-hidden": true,
      "data-active": dataAttr(isActive)
    }),
    inputProps: normalize.input({
      ...parts.input.attrs,
      id: dom.getInputId(state.context),
      type: "checkbox",
      required: isRequired,
      defaultChecked: isChecked,
      disabled: trulyDisabled,
      "data-disabled": dataAttr(isDisabled),
      "aria-readonly": ariaAttr(isReadOnly),
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-invalid": isInvalid,
      "aria-describedby": ariaDescribedBy,
      name,
      form,
      value,
      style: visuallyHiddenStyle,
      onChange() {
        send({ type: "TOGGLE" });
      },
      onBlur() {
        send({ type: "SET_FOCUSED", value: false });
      },
      onFocus() {
        send({ type: "SET_FOCUSED", value: true });
      },
      onKeyDown(event) {
        if (event.key === " ") {
          send({ type: "SET_ACTIVE", value: true });
        }
      },
      onKeyUp(event) {
        if (event.key === " ") {
          send({ type: "SET_ACTIVE", value: false });
        }
      }
    })
  };
}

export {
  connect
};
